import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { eecsGoTableFields } from '../../constants/accountTable';
import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  InputNumber,
  message,
  notification,
  Popconfirm,
  Table,
} from 'antd';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { NAMESPACES, tCommon } from '../../i18n';
import { TableActions, TableRowActions } from '../';
import { goBack } from '../../utils/gotoLink';
import {
  translateFuelTypeClassificator,
  translateProductionTechnologyClassificator,
} from '../../utils/translateHelpers';
import dayjs from 'dayjs';
import classifiersStore from '../../utils/classifiersStore';

export const CancelEecsGOsTable = ({
  performAction,
  validateAction,
  goAmountTotal,
  isCancelAvailable,
  isUsersOtherAccount,
  eecsGoRows: initialEecsGoRows,
}) => {
  const { t } = useTranslation(NAMESPACES.cancelingCertificates);
  const [eecsGoRows, setEecsGoRows] = useState(initialEecsGoRows);
  const [rowErrors, setRowErrors] = useState({});
  const [certificatesAmount] = useState(undefined);

  const onAmountChange = useCallback((value, rowId) => {
    setEecsGoRows((prev) => {
      const rows = prev || [];

      const parsed = parseInt(value, 10);
      const nextQuantity = Number.isNaN(parsed) ? 1 : parsed;

      const index = rows.findIndex(
        (item) => item[eecsGoTableFields.ID] === rowId
      );
      if (index === -1) return prev;

      const updates = { [eecsGoTableFields.QUANTITY]: nextQuantity };
      const nextRows = rows.slice();
      nextRows[index] = { ...nextRows[index], ...updates };

      return nextRows;
    });
  }, []);

  const onRemove = useCallback(
    (row) => {
      setEecsGoRows((prev) => {
        const rows = prev || [];

        const totalBefore = rows.length;
        if (totalBefore <= 1) {
          message.warning(t('oneRequired'));
          return prev;
        }

        return rows.filter((item) => item.id !== row.id);
      });
    },
    [t]
  );

  const isAmountInvalid = useCallback(
    (total, amount) => !_.isNil(total) && total < amount,
    []
  );

  const tryPerformAction = useCallback(async (action) => {
    try {
      const result = await action();
      return { result, hasError: false };
    } catch {
      return { result: null, hasError: true };
    }
  }, []);

  const handleOutcome = useCallback(({ result, hasError, t }) => {
    const isSuccess = !(result instanceof Error) && !hasError && !result?.error;

    const messageKey = isSuccess
      ? 'successPerformCancel'
      : 'failurePerformCancel';

    if (isSuccess) {
      message.success(t(messageKey));
      return goBack();
    }

    notification.warning({ duration: 0, message: t(messageKey) });
  }, []);

  const handlePerformClick = useCallback(async () => {
    if (isAmountInvalid(goAmountTotal, certificatesAmount)) {
      return message.error(t('errorAmount'));
    }

    if (validateAction?.(isUsersOtherAccount)) {
      return;
    }

    const { result, hasError } = await tryPerformAction(() =>
      performAction(eecsGoRows)
    );

    if (result?.error?.rowErrors) {
      return setRowErrors(result.error.rowErrors);
    }

    handleOutcome({ result, hasError, t });
  }, [
    t,
    performAction,
    goAmountTotal,
    validateAction,
    isUsersOtherAccount,
    eecsGoRows,
    certificatesAmount,
    isAmountInvalid,
    tryPerformAction,
    handleOutcome,
  ]);
  const formatWithDayjs = (s) =>
    typeof s === 'string' && dayjs(s, 'YYYY-MM-DD', true).isValid()
      ? dayjs(s, 'YYYY-MM-DD').format('DD.MM.YYYY')
      : s;
  const formatFeedstock = (row) =>
    classifiersStore.getClassifierByKey(
      `FUEL_TYPE.${row.fuelTypeClassifier}.FEEDSTOCK.${row.feedstockClassifier}`
    ) +
    ' (' +
    row.energySource +
    ')';
  const formatProductionTechnology = (productionTechnology) =>
    translateProductionTechnologyClassificator(productionTechnology) +
    ' (' +
    productionTechnology +
    ')';
  const columns = useMemo(() => {
    return [
      {
        title: t('table.productionUnitEecsCode'),
        dataIndex: eecsGoTableFields.DEVICE_EECS_CODE,
      },
      {
        title: t('table.productionPeriod'),
        dataIndex: eecsGoTableFields.PRODUCTION_PERIOD,
      },
      {
        title: t('table.expires'),
        dataIndex: eecsGoTableFields.EXPIRATION_TIME,
        render: formatWithDayjs,
      },
      {
        title: t('table.fuelType'),
        dataIndex: eecsGoTableFields.FUEL_TYPE,
        render: translateFuelTypeClassificator,
      },
      {
        title: t('table.feedstock'),
        dataIndex: eecsGoTableFields.FEEDSTOCK,
        render: (_, record) => formatFeedstock(record),
      },
      {
        title: t('table.productionTechnology'),
        dataIndex: eecsGoTableFields.PRODUCTION_TECHNOLOGY,
        render: formatProductionTechnology,
      },
      {
        title: t('table.quantity'),
        dataIndex: eecsGoTableFields.QUANTITY,
        render: (value, record) => {
          const rowId = record[eecsGoTableFields.ID];
          return (
            <InputNumber
              min={1}
              max={record[eecsGoTableFields.MAX_QUANTITY]}
              value={value}
              onChange={(numberValue) => onAmountChange(numberValue, rowId)}
            />
          );
        },
      },
      {
        title: t('table.actions'),
        dataIndex: eecsGoTableFields.GO_SEARCH_RESPONSE_ROW_ID,
        render: (row) => (
          <TableRowActions>
            <Button
              shape="circle"
              icon={<DeleteOutlined />}
              size="small"
              type="primary"
              onClick={() => onRemove(row)}
            />
          </TableRowActions>
        ),
      },
    ];
  }, [t, onAmountChange, onRemove]);
  const totalCertificates = eecsGoRows.reduce(
    (sum, eecsGo) => sum + eecsGo.quantity,
    0
  );
  return (
    <>
      <Table
        rowKey={eecsGoTableFields.GO_SEARCH_RESPONSE_ROW_ID}
        rowClassName={(row) =>
          rowErrors[row.goSearchResponseRowId] ? 'row-error' : ''
        }
        dataSource={eecsGoRows}
        columns={columns}
        pagination={false}
        scroll={{ x: true }}
        footer={() => (
          <div className="global-align-right">
            {t('chooseCertificates', {
              certificates: totalCertificates,
            })}
          </div>
        )}
      />
      <TableActions>
        <Button onClick={goBack}>{t('buttonBack')}</Button>
        <Popconfirm
          title={t('popconfirmTitleCancel')}
          onConfirm={handlePerformClick}
          okText={tCommon('popconfirm.buttonYes')}
          cancelText={tCommon('popconfirm.buttonNo')}
        >
          {isCancelAvailable && (
            <Button type="primary" className="global-margin-left-10">
              {t('buttonPerform')}
            </Button>
          )}
        </Popconfirm>
      </TableActions>
    </>
  );
};

CancelEecsGOsTable.propTypes = {
  performAction: PropTypes.func.isRequired,
  validateAction: PropTypes.func,
  goAmountTotal: PropTypes.number,
  isCancelAvailable: PropTypes.bool,
  isUsersOtherAccount: PropTypes.bool,
  eecsGoRows: PropTypes.array.isRequired,
};
